import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  VStack,
  Link,
  Button,
  Box,
  Text,
  Collapse,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { supabasePublic, getSupabase } from "../supabaseClient";
import HistoryModal from "../HistoryModal";
import { trackUserEvent, flushUserEvents } from "../../utils/userEventTracker";
import { isImpersonating } from "../../utils/impersonation";

const SideNav = () => {
  const [schemas, setSchemas] = useState([]);
  const [activeSchema, setActiveSchema] = useState(null);
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  useEffect(() => {
    const fetchSchemas = async () => {
      setLoading(true);
      try {
        // Use the active client (target's during view-as, admin's normally)
        // so the displayed schemas reflect whichever user the navbar is
        // currently rendering for. Read public.users explicitly via
        // .schema('public') because tenant-schema copies of users.schema can
        // drift in their comma ordering — public is the canonical source.
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data, error } = await supabase
          .schema("public")
          .from("users")
          .select("schema")
          .eq("id", session.user.id)
          .single();

        if (!error && data?.schema) {
          const parts = data.schema
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          setSchemas(parts);
          setActiveSchema(parts[0]);
        } else {
          setSchemas(["public"]);
          setActiveSchema("public");
        }
      } catch (e) {
        console.error("Error fetching schemas:", e);
        setSchemas(["public"]);
        setActiveSchema("public");
      } finally {
        setLoading(false);
      }
    };
    fetchSchemas();
  }, []);

  const handleSwitchSchema = async (selected) => {
    if (selected === activeSchema) return;
    setSwitching(true);
    try {
      const pub = supabasePublic();
      const {
        data: { session },
      } = await pub.auth.getSession();
      if (!session?.user) {
        setSwitching(false);
        return;
      }

      // Ensure user has a row in the target schema's users table (copy from public if not)
      if (selected !== "public") {
        const { data: userRow, error: fetchError } = await pub
          .from("users")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (fetchError || !userRow) {
          console.error("Error fetching user row for schema copy:", fetchError);
          setSwitching(false);
          return;
        }

        const { error: rpcError } = await pub.rpc("upsert_user_into_schema", {
          target_schema: selected,
          user_data: userRow,
        });

        if (rpcError) {
          console.error("Error ensuring user in target schema:", rpcError);
          setSwitching(false);
          return;
        }
      }

      // Reorder: put selected first, then the rest in their original order
      const reordered = [selected, ...schemas.filter((s) => s !== selected)];
      const newSchemaValue = reordered.join(",");

      const { error } = await pub
        .from("users")
        .update({ schema: newSchemaValue })
        .eq("id", session.user.id);

      if (error) {
        console.error("Error updating schema:", error);
        setSwitching(false);
        return;
      }

      // Track before reload so the event survives the page refresh. We emit
      // into the CURRENT schema (the one we're leaving) because the reload
      // will reinitialize the tracker against the new schema.
      trackUserEvent("schema_switch", {
        metadata: { from: activeSchema, to: selected },
      });
      try {
        await flushUserEvents();
      } catch (_) {}

      // Reload the page so all clients reinitialize with the new active schema
      window.location.reload();
    } catch (e) {
      console.error("Error switching schema:", e);
      setSwitching(false);
    }
  };

  const hasMultipleSchemas = schemas.length > 1;

  return (
    <>
      <VStack spacing={4} align="stretch">
        <Link as={RouterLink} to="/recent-audios">
          Recent Audios
        </Link>
        <Link as={RouterLink} to="/search">
          Search
        </Link>
        <Link as={RouterLink} to="/settings">
          Settings
        </Link>
        <Button
          variant="ghost"
          width="100%"
          justifyContent="flex-start"
          fontWeight="normal"
          onClick={() => setIsHistoryOpen(true)}
        >
          Activity History
        </Button>
        <Link as={RouterLink} to="/help-desk">
          Help Desk
        </Link>

        {hasMultipleSchemas && (
          <Box>
            <Button
              variant="ghost"
              width="100%"
              justifyContent="flex-start"
              fontWeight="normal"
              px={0}
              onClick={() => setIsSchemaOpen(!isSchemaOpen)}
            >
              Switch Schema {isSchemaOpen ? "▲" : "▼"}
            </Button>
            <Collapse in={isSchemaOpen} animateOpacity>
              <Box pl={4} pt={2}>
                {loading ? (
                  <Spinner size="sm" />
                ) : switching ? (
                  <Spinner size="sm" />
                ) : (
                  <VStack align="stretch" spacing={1}>
                    {schemas.map((s) => (
                      <Tooltip
                        key={s}
                        label={isImpersonating() ? "Schema switching disabled while viewing as another user" : ""}
                        isDisabled={!isImpersonating()}
                        hasArrow
                        placement="right"
                      >
                        <Button
                          size="sm"
                          variant={s === activeSchema ? "solid" : "outline"}
                          colorScheme={s === activeSchema ? "blue" : "gray"}
                          onClick={() => handleSwitchSchema(s)}
                          isDisabled={s === activeSchema || isImpersonating()}
                        >
                          <Text>
                            {s}
                            {s === activeSchema && " (active)"}
                          </Text>
                        </Button>
                      </Tooltip>
                    ))}
                  </VStack>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        <Link as={RouterLink} to="/terms-of-service">
          Terms of Service
        </Link>
        <Link as={RouterLink} to="/signout">
          Sign Out
        </Link>
      </VStack>

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />
    </>
  );
};

export default SideNav;
